// /transcription API
//  - WebSocket connection for real-time transcription
//  - Settings management for transcription parameters

import { WSClient } from "./apiBase/wsClient.js";

export class TranscriptionWS {
  /** @param {{wsURL: string}} opts */
  constructor(opts) {
    this.wsURL = opts.wsURL;
    this.connections = new Map();
  }

  /**
   * Connect to transcription WebSocket for a specific device
   * @param {string} deviceHexId - The device ID to connect to
   * @param {Object} callbacks - Event callbacks
   * @param {Function} callbacks.onTranscription - Called when transcription result is received
   * @param {Function} callbacks.onPartial - Called when a live partial update is received
   * @param {Function} callbacks.onPartialEnd - Called when partial should be cleared (e.g. empty commit)
   * @param {Function} callbacks.onError - Called on connection errors
   * @param {Function} callbacks.onClose - Called when connection closes
   */
  connect(deviceHexId, callbacks = {}, options = {}) {
    const url = `${this.wsURL}/transcription/ws/${deviceHexId}`;
    const forceReconnect = options.forceReconnect === true;

    if (forceReconnect) {
      this.disconnect(deviceHexId);
    } else {
      const existingConnection = this.connections.get(deviceHexId);
      if (existingConnection) {
        return existingConnection;
      }
    }

    const ws = new WSClient(url, {
      onMessage: (data) => {
        const msgType = (data && (data.type || data.Type)) ? String(data.type || data.Type).toLowerCase() : '';
        // console.log('[TranscriptionWS] Msg:', msgType, data);
        if (msgType === 'result' && callbacks.onTranscription) {
          console.log('[TranscriptionWS] Handling Result');
          callbacks.onTranscription(data);
        } else if (msgType === 'partial' && callbacks.onPartial) {
          console.log('[TranscriptionWS] Handling Partial');
          callbacks.onPartial(data);
        } else if (msgType === 'partial_end' && callbacks.onPartialEnd) {
          callbacks.onPartialEnd(data);
        } else if (msgType === 'error' && callbacks.onAlert) {
          console.log('[TranscriptionWS] Handling Error/Alert:', data);
          callbacks.onAlert(data);
        }
      },
      onOpen: callbacks.onOpen,
      onError: callbacks.onError,
      onClose: (event) => {
        this.connections.delete(deviceHexId);
        callbacks.onClose?.(event);
      },
    });

    this.connections.set(deviceHexId, ws);

    return ws;
  }

  /**
   * Disconnect from the transcription WebSocket
   */
  disconnect(deviceHexId) {
    if (deviceHexId) {
      const ws = this.connections.get(deviceHexId);
      if (ws) {
        ws.close();
        this.connections.delete(deviceHexId);
      }
      return;
    }

    for (const ws of this.connections.values()) {
      ws.close();
    }
    this.connections.clear();
  }

  /**
   * Check if currently connected
   */
  isConnected() {
    for (const ws of this.connections.values()) {
      if (ws?.ws?.readyState === WebSocket.OPEN) {
        return true;
      }
    }
    return false;
  }
}

// Transcription settings API functions
export const getTranscriptionSettings = async (baseURL) => {
  try {
    const url = `${baseURL}/transcription/settings`;
    console.log('[API] Fetching transcription settings from:', url);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('[API] Response status:', response.status);
    console.log('[API] Response headers:', response.headers);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const text = await response.text();
    console.log('[API] Raw response:', text);

    const data = JSON.parse(text);
    console.log('[API] Parsed data:', data);

    return data.settings;
  } catch (error) {
    console.error('Error fetching transcription settings:', error);
    throw error;
  }
};

export const updateTranscriptionSettings = async (settings, baseURL) => {
  try {
    const url = `${baseURL}/transcription/settings`;
    console.log('[API] Updating transcription settings at:', url);
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating transcription settings:', error);
    throw error;
  }
};

export const setTranscriptionPreset = async (preset, baseURL) => {
  try {
    const url = `${baseURL}/transcription/settings/preset/${preset}`;
    console.log('[API] Setting preset:', preset, 'at:', url);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('[API] Preset response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const text = await response.text();
    console.log('[API] Preset raw response:', text);

    const data = JSON.parse(text);
    console.log('[API] Preset parsed data:', data);

    return data;
  } catch (error) {
    console.error('Error setting transcription preset:', error);
    throw error;
  }
};
